import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchExams,
  deriveUniqueTermIds,
  filterInvalidExamSelections
} from '../utils/utils';
import { AppDispatch, RootState } from '../../../redux/store';
import { defaultAcademicYear } from '../../../constants/academicYears';
import { SchoolClassesConfig } from '../../../types/schoolConfigType';
import { fetchSchools } from '../../../redux/slices/School';
import { getSchoolClassesConfig } from '../../../apis/schoolConfig';
import {
  ExamOption,
  UseMarksAnalyticsProps
} from '../types/MarksAnalyticsTypes';
import { SnackbarSeverityType } from '../../../enums';

export const useMarksAnalytics = ({ showToast }: UseMarksAnalyticsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.profile);
  const { schools } = useSelector((state: RootState) => state.school);
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [classesConfig, setClassesConfig] =
    useState<SchoolClassesConfig | null>(null);
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [examsOptions, setExamsOptions] = useState<ExamOption[]>([]);
  const [derivedTermIds, setDerivedTermIds] = useState<string[]>([]);
  const [selectedExamIds, setSelectedExamIds] = useState<string[]>([]);

  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingExams, setIsLoadingExams] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const gradeOptions = classesConfig?.grades || [];
  const divisionOptions = useMemo(() => {
    if (!classesConfig || !grade) return [];
    return classesConfig.classes
      .filter((classItem) => classItem.grade === grade)
      .map((classItem) => classItem.division);
  }, [classesConfig, grade]);

  useEffect(() => {
    if (!schools || !schools.length) {
      dispatch(fetchSchools());
    }
  }, [dispatch, schools]);

  useEffect(() => {
    if (!selectedSchool) {
      setClassesConfig(null);
      setGrade('');
      setDivision('');
      return;
    }
    setIsLoadingClasses(true);
    getSchoolClassesConfig(selectedSchool)
      .then((response: any) => {
        const config = response?.data || response;
        setClassesConfig(config);
        if (
          config &&
          Array.isArray(config.grades) &&
          config.grades.length === 0
        ) {
          showToast(
            'No grades configured for the selected school',
            SnackbarSeverityType.error
          );
        }
      })
      .catch(() =>
        showToast('Failed to load classes', SnackbarSeverityType.error)
      )
      .finally(() => setIsLoadingClasses(false));
    // eslint-disable-next-line
  }, [selectedSchool]);

  useEffect(() => {
    if (!selectedSchool || !grade || !division || !academicYear) {
      setExamsOptions([]);
      setSelectedExamIds([]);
      setDerivedTermIds([]);
      return;
    }
    setIsLoadingExams(true);
    fetchExams(grade, division, academicYear, selectedSchool)
      .then((mapped) => {
        setExamsOptions(mapped);
        const uniqueTermIds = deriveUniqueTermIds(mapped);
        setDerivedTermIds(uniqueTermIds);
        setSelectedExamIds((prev) => filterInvalidExamSelections(prev, mapped));
      })
      .catch(() => {
        setExamsOptions([]);
        setSelectedExamIds([]);
        setDerivedTermIds([]);
        showToast('Failed to load exams', SnackbarSeverityType.error);
      })
      .finally(() => setIsLoadingExams(false));
    // eslint-disable-next-line
  }, [selectedSchool, grade, division, academicYear]);

  const onSchoolChange = (value: string) => {
    setSelectedSchool(value);
    setGrade('');
    setDivision('');
    setDerivedTermIds([]);
    setSelectedExamIds([]);
    setExamsOptions([]);
  };

  const onGradeChange = (value: string) => {
    setGrade(value);
    setDivision('');
    setDerivedTermIds([]);
    setSelectedExamIds([]);
    setExamsOptions([]);
  };

  const onDivisionChange = (value: string) => {
    setDivision(value);
    setDerivedTermIds([]);
    setSelectedExamIds([]);
    setExamsOptions([]);
  };

  const canGenerateOrDownload = !!(
    selectedSchool &&
    academicYear &&
    grade &&
    division &&
    derivedTermIds.length
  );

  return {
    academicYear,
    setAcademicYear,
    selectedSchool,
    onSchoolChange,
    grade,
    onGradeChange,
    division,
    onDivisionChange,
    examsOptions,
    selectedExamIds,
    setSelectedExamIds,
    derivedTermIds,
    isLoadingClasses,
    isLoadingExams,
    isGenerating,
    setIsGenerating,
    isDownloading,
    setIsDownloading,
    canGenerateOrDownload,
    gradeOptions,
    divisionOptions,
    user,
    schools,
    showToast
  };
};
